


import React, { useState, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import {
    Eye,
    FileText,
    X,
    Upload,
    Plus,
    FileUp,
    FileCheck,
    FileSpreadsheet,
    Paperclip,
    Trash2,
    Loader,
    AlertCircle,
} from "lucide-react";

import "./DocumentModel.css";

import { axiosInstance2 } from "../config/axiosInstance";
import {
    CSSForSelectModern,
    handleErrorToast,
    ProgressModal,
} from "./CustomFunctions";

import { BASE_URL } from "../config/axios";
import axios from "axios";
import { DeleteDustbin } from "./AllSvg";

const ALLOWED_EXT = /\.(zip|pdf|xls|xlsx|png|gif)$/i;
const ACCEPT = ".zip,.pdf,.xls,.xlsx,.png,.gif";

// One document type is selected per file. Values are sent unchanged to the API.
const DOCUMENT_TYPE_OPTIONS = [
    { value: "Quotation", label: "Quotation" },
    { value: "Work-Order", label: "Work-Order" },
    { value: "Test Plan", label: "Test Plan" },
    { value: "letter", label: "letter" },
    { value: "Reports", label: "Reports" },
];

/*
|--------------------------------------------------------------------------
| CHUNK SIZE
|--------------------------------------------------------------------------
|
| 5 MB per chunk
|
*/

const CHUNK_SIZE = 5 * 1024 * 1024;

const formatSize = (bytes = 0) =>
    bytes < 1024 * 1024
        ? `${Math.max(1, Math.round(bytes / 1024))} KB`
        : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const randomCode = (len = 8) => {
    const chars =
        "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

    return Array.from(
        { length: len },
        () => chars[Math.floor(Math.random() * chars.length)]
    ).join("");
};

/*
|--------------------------------------------------------------------------
| UNIQUE FILE ID
|--------------------------------------------------------------------------
*/

const generateFileId = () => {
    return `${Date.now()}_${randomCode(16)}`;
};

/*
|--------------------------------------------------------------------------
| DELETE DOCUMENT COMPONENT
|--------------------------------------------------------------------------
*/

const DeleteDocuments = ({ i, getDocument }) => {
    const [show, setshow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setshow(false);

    const handleShow = () => setshow(true);

    const handleDelete = async (e) => {
        e.preventDefault();

        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading(
            "Deleting: please wait..."
        );

        let progress = 0;

        const interval = setInterval(() => {
            progress += 10;

            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }

            setUploadProgress(progress);
        }, 100);

        try {
            const ress = await axios.delete(
                `${BASE_URL}/labmonitoring/projectdocument/${i.id}/delete/`
            );

            if (ress.status === 200) {
                await getDocument();

                toast.dismiss(loadingToastId);

                toast.success("Data deleted successfully.", {
                    autoClose: 1000,
                    pauseOnHover: true,
                    closeOnClick: true,
                });

                setshow(false);
            } else {
                throw new Error(
                    "Unexpected response status: " +
                    ress.status
                );
            }
        } catch (err) {
            clearInterval(interval);

            setUploadProgress(0);

            handleErrorToast(err);
        } finally {
            clearInterval(interval);

            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);

            setLoading(false);

            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <button
                title="Delete Document"
                className="model-delete-button-modern action-icon"
                style={{
                    backgroundColor: "#00000075",
                }}
                onClick={handleShow}
            >
                <DeleteDustbin modernButton="true" />
            </button>

            {showProgressModal ? (
                <ProgressModal
                    show={showProgressModal}
                    progress={uploadProgress}
                    usedFor="Delete"
                />
            ) : (
                <Modal
                    show={show}
                    onHide={handleClose}
                    dialogClassName="modern-required-modal"
                    centered
                >
                    <Modal.Header className="modern-modal-header requirement-modal-header">
                        <div className="header-content">
                            <div className="requirement-header-icon">
                                <Trash2 size={22} />
                            </div>

                            <div>
                                <Modal.Title>
                                    Delete Document
                                </Modal.Title>

                                <p
                                    className="header-subtitle"
                                    style={{
                                        color: "white",
                                    }}
                                >
                                    This action cannot be undone
                                </p>
                            </div>
                        </div>

                        <button
                            className="close-btn"
                            onClick={handleClose}
                        >
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body requirement-modal-body">
                        <div
                            className="requirement-mode-summary"
                            style={{
                                marginBottom: "20px",
                            }}
                        >
                            <AlertCircle size={16} />

                            <span>
                                Are you sure you want to
                                delete{" "}
                                <strong>
                                    {i.document_name}
                                </strong>
                                ?
                            </span>
                        </div>

                        <div
                            className="submit-buttons"
                            style={{
                                justifyContent: "flex-end",
                            }}
                        >
                            <button
                                type="button"
                                className="btn-secondaryy"
                                onClick={handleClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                className="btn-primaryy"
                                style={{
                                    background: "#dc3545",
                                    borderColor: "#dc3545",
                                }}
                                onClick={handleDelete}
                                disabled={loading}
                            >
                                {loading ? (
                                    <Loader
                                        size={18}
                                        className="spinning"
                                    />
                                ) : (
                                    <Trash2 size={18} />
                                )}

                                <span>
                                    {loading
                                        ? "Deleting..."
                                        : "Delete"}
                                </span>
                            </button>
                        </div>

                        <ToastContainer
                            position="top-center"
                            autoClose={1000}
                            hideProgressBar={false}
                            newestOnTop
                            closeOnClick
                            rtl={false}
                            pauseOnFocusLoss
                            draggable
                            pauseOnHover
                        />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

/*
|--------------------------------------------------------------------------
| DOCUMENT MODEL
|--------------------------------------------------------------------------
*/

const DocumentModel = ({
    project,
    title,
    getDocument,
    existingDocuments = [],
}) => {
    const idRef = useRef(0);

    const newRow = () => ({
        id: ++idRef.current,
        document_name: "",
        document_file: null,
        original_filename: "",
        document_type: "",
        project,
        displayName: "",
        size: 0,
    });

    const [show, setShow] = useState(false);

    const [tab, setTab] = useState("add");

    const [rows, setRows] = useState(() => [
        newRow(),
    ]);

    const [errors, setErrors] = useState({});

    const [dragOver, setDragOver] = useState(null);

    const [loading, setLoading] = useState(false);

    const [uploadProgress, setUploadProgress] =
        useState(0);

    const fileInputRefs = useRef({});

    const [documentList, setDocumentList] =
        useState([]);

    /*
    |--------------------------------------------------------------------------
    | DOCUMENT TYPE
    |--------------------------------------------------------------------------
    */

    // Keep labels readable for documents uploaded with the previous type values.
    const typeLabel = (value) => {
        const option = DOCUMENT_TYPE_OPTIONS.find((o) => o.value === value);
        if (option) return option.label;

        if (value === "actual") {
            return title === "LOA & Certificate Document"
                ? "LOA Copy"
                : "Actual Document";
        }
        if (value === "boq") {
            return title === "LOA & Certificate Document"
                ? "Certificate Copy"
                : "BOQ Document";
        }
        return value || "—";
    };

    /*
    |--------------------------------------------------------------------------
    | RESET
    |--------------------------------------------------------------------------
    */

    const reset = () => {
        setRows([newRow()]);
        setErrors({});
        setUploadProgress(0);
    };

    useEffect(() => {
        reset();

        // eslint-disable-next-line
    }, [project]);

    /*
    |--------------------------------------------------------------------------
    | CLOSE MODAL
    |--------------------------------------------------------------------------
    */

    const handleClose = () => {
        if (loading) return;

        setShow(false);

        setTab("add");

        reset();
    };

    /*
    |--------------------------------------------------------------------------
    | ERROR HANDLING
    |--------------------------------------------------------------------------
    */

    const clearError = (key) =>
        setErrors((prev) => {
            if (!prev[key]) return prev;

            const next = {
                ...prev,
            };

            delete next[key];

            return next;
        });

    /*
    |--------------------------------------------------------------------------
    | UPDATE ROW
    |--------------------------------------------------------------------------
    */

    const updateRow = (id, patch) => {
        setRows((prev) =>
            prev.map((row) =>
                row.id === id
                    ? {
                        ...row,
                        ...patch,
                    }
                    : row
            )
        );
    };

    /*
    |--------------------------------------------------------------------------
    | INPUT CHANGE
    |--------------------------------------------------------------------------
    */

    const handleInputChange = (
        id,
        name,
        value
    ) => {
        updateRow(id, {
            [name]: value,
        });

        if (String(value).trim() !== "") {
            clearError(`${id}-${name}`);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | FILE SELECT
    |--------------------------------------------------------------------------
    |
    | IMPORTANT:
    |
    | Yahan actual File object ko rename nahi kar rahe.
    |
    */

    const processFile = (id, file) => {
        if (!file) return;

        if (!ALLOWED_EXT.test(file.name)) {
            toast.error(
                "Only ZIP, PDF, PNG, GIF and Excel files are allowed."
            );

            return;
        }

        updateRow(id, {
            document_file: file,

            original_filename: file.name,

            displayName: file.name,

            size: file.size,
        });

        clearError(
            `${id}-document_file`
        );
    };

    /*
    |--------------------------------------------------------------------------
    | CLEAR FILE
    |--------------------------------------------------------------------------
    */

    const clearFile = (id) => {
        updateRow(id, {
            document_file: null,
            original_filename: "",
            displayName: "",
            size: 0,
        });

        if (
            fileInputRefs.current[id]
        ) {
            fileInputRefs.current[
                id
            ].value = "";
        }
    };

    /*
    |--------------------------------------------------------------------------
    | ADD ROW
    |--------------------------------------------------------------------------
    */

    const addRow = () => {
        setRows((prev) => [
            ...prev,
            newRow(),
        ]);
    };

    /*
    |--------------------------------------------------------------------------
    | REMOVE ROW
    |--------------------------------------------------------------------------
    */

    const removeRow = (id) => {
        setRows((prev) =>
            prev.filter(
                (row) => row.id !== id
            )
        );

        delete fileInputRefs.current[
            id
        ];
    };

    /*
    |--------------------------------------------------------------------------
    | VALIDATION
    |--------------------------------------------------------------------------
    */

    const validate = () => {
        const next = {};

        rows.forEach((row) => {
            if (
                !row.document_name.trim()
            ) {
                next[
                    `${row.id}-document_name`
                ] =
                    "Enter a document name.";
            }

            if (!row.document_type) {
                next[
                    `${row.id}-document_type`
                ] =
                    "Select a document type.";
            }

            if (!row.document_file) {
                next[
                    `${row.id}-document_file`
                ] =
                    "Attach a file.";
            }
        });

        setErrors(next);

        return (
            Object.keys(next).length ===
            0
        );
    };

    /*
    |--------------------------------------------------------------------------
    | GET DOCUMENT LIST
    |--------------------------------------------------------------------------
    */

    const getDocumentList =
        async () => {
            try {
                const res =
                    await axios.get(
                        `${BASE_URL}/labmonitoring/projectdocumentfilterbyproject/${project}/`
                    );

                setDocumentList(
                    res.data
                );
            } catch (err) {
                toast.error(
                    "Failed to fetch document list."
                );
            }
        };

    useEffect(() => {
        if (show === true) {
            getDocumentList();
        }
    }, [show, project]);

    /*
    |--------------------------------------------------------------------------
    | UPLOAD SINGLE FILE IN CHUNKS
    |--------------------------------------------------------------------------
    |
    | API 1
    |
    | POST /project-document/chunk-upload/
    |
    */

    const uploadFileInChunks =
        async (
            row,
            onProgress
        ) => {
            const file =
                row.document_file;

            if (!file) {
                throw new Error(
                    "File not found."
                );
            }

            /*
            --------------------------------------------------------------
            UNIQUE FILE ID
            --------------------------------------------------------------
            */

            const fileId =
                generateFileId();

            /*
            --------------------------------------------------------------
            TOTAL CHUNKS
            --------------------------------------------------------------
            */

            const totalChunks =
                Math.ceil(
                    file.size /
                    CHUNK_SIZE
                );

            /*
            --------------------------------------------------------------
            UPLOAD CHUNKS ONE BY ONE
            --------------------------------------------------------------
            */

            for (
                let chunkIndex = 0;
                chunkIndex <
                totalChunks;
                chunkIndex++
            ) {
                const start =
                    chunkIndex *
                    CHUNK_SIZE;

                const end =
                    Math.min(
                        start +
                        CHUNK_SIZE,
                        file.size
                    );

                const chunk =
                    file.slice(
                        start,
                        end
                    );

                const formData =
                    new FormData();

                formData.append(
                    "file",
                    chunk
                );

                formData.append(
                    "file_id",
                    fileId
                );

                formData.append(
                    "chunk_index",
                    chunkIndex
                );

                formData.append(
                    "total_chunks",
                    totalChunks
                );

                formData.append(
                    "original_filename",
                    row.original_filename ||
                    file.name
                );

                // Make the selected type available to the chunk upload endpoint.
                formData.append("document_type", row.document_type);

                /*
                ----------------------------------------------------------
                CHUNK UPLOAD API
                ----------------------------------------------------------
                */

                await axiosInstance2.post(
                    "/labmonitoring/project-document/chunk-upload/",
                    formData
                );

                /*
                ----------------------------------------------------------
                FILE PROGRESS
                ----------------------------------------------------------
                */

                const progress =
                    Math.round(
                        ((chunkIndex +
                            1) /
                            totalChunks) *
                        100
                    );

                onProgress?.(
                    progress
                );
            }

            /*
            --------------------------------------------------------------
            API 2
            COMPLETE UPLOAD
            --------------------------------------------------------------
            */

            const completeResponse =
                await axiosInstance2.post(
                    "/labmonitoring/project-document/complete-upload/",
                    {
                        file_id:
                            fileId,
                    },
                    {
                        headers: {
                            "Content-Type":
                                "application/json",
                        },
                    }
                );

            /*
            Expected:

            {
                file_name: "...",
                file_path: "...",
                file_url: "..."
            }
            */

            return completeResponse.data;
        };

    /*
    |--------------------------------------------------------------------------
    | SUBMIT
    |--------------------------------------------------------------------------
    */

    const handleSubmit =
        async (e) => {
            e.preventDefault();

            if (
                !validate() ||
                loading
            ) {
                return;
            }

            setLoading(true);

            setUploadProgress(0);

            const toastId =
                toast.loading(
                    "Uploading documents..."
                );

            try {
                /*
                ----------------------------------------------------------
                FINAL FILE ARRAY
                ----------------------------------------------------------
                */

                const uploadedFiles =
                    [];

                /*
                ----------------------------------------------------------
                UPLOAD EACH FILE
                ----------------------------------------------------------
                */

                for (
                    let fileIndex = 0;
                    fileIndex <
                    rows.length;
                    fileIndex++
                ) {
                    const row =
                        rows[
                        fileIndex
                        ];

                    /*
                    ------------------------------------------------------
                    CHUNK UPLOAD + COMPLETE
                    ------------------------------------------------------
                    */

                    const completedFile =
                        await uploadFileInChunks(
                            row,
                            (
                                fileProgress
                            ) => {
                                /*
                                ------------------------------------------
                                CALCULATE OVERALL PROGRESS
                                ------------------------------------------
                                */

                                const overallProgress =
                                    ((
                                        fileIndex +
                                        fileProgress /
                                        100
                                    ) /
                                        rows.length) *
                                    100;

                                setUploadProgress(
                                    Math.round(
                                        overallProgress
                                    )
                                );
                            }
                        );

                    /*
                    ------------------------------------------------------
                    SAVE COMPLETE API RESULT
                    ------------------------------------------------------
                    */

                    uploadedFiles.push(
                        {
                            file_path:
                                completedFile.file_path,

                            document_name:
                                row.document_name,

                            original_filename:
                                completedFile.file_name ||
                                row.original_filename ||
                                row
                                    .document_file
                                    ?.name,

                            // Store document_type with each confirmed document.
                            document_type:
                                row.document_type,
                        }
                    );
                }

                /*
                ----------------------------------------------------------
                API 3
                BULK CONFIRM
                ----------------------------------------------------------
                */

                const confirmResponse =
                    await axiosInstance2.post(
                        "/labmonitoring/project-document/bulk-confirm/",
                        {
                            project:
                                project,

                            files: uploadedFiles,
                        },
                        {
                            headers: {
                                "Content-Type":
                                    "application/json",
                            },
                        }
                    );

                /*
                ----------------------------------------------------------
                SUCCESS
                ----------------------------------------------------------
                */

                if (
                    confirmResponse.status >=
                    200 &&
                    confirmResponse.status <
                    300
                ) {
                    setUploadProgress(
                        100
                    );

                    toast.dismiss(
                        toastId
                    );

                    toast.success(
                        rows.length >
                            1
                            ? `${rows.length} documents uploaded successfully.`
                            : "Document uploaded successfully.",
                        {
                            autoClose: 1500,
                        }
                    );

                    /*
                    ------------------------------------------------------
                    REFRESH PARENT DOCUMENT LIST
                    ------------------------------------------------------
                    */

                    if (getDocument) {
                        await getDocument();
                    }

                    /*
                    ------------------------------------------------------
                    REFRESH CURRENT MODAL LIST
                    ------------------------------------------------------
                    */

                    await getDocumentList();

                    /*
                    ------------------------------------------------------
                    RESET MODAL
                    ------------------------------------------------------
                    */

                    setShow(false);

                    setTab("add");

                    reset();
                }
            } catch (err) {
                console.error(
                    "Document upload error:",
                    err
                );

                toast.dismiss(
                    toastId
                );

                const message =
                    err?.response?.data
                        ?.detail ||
                    err?.response?.data
                        ?.message ||
                    err?.message ||
                    "Upload failed. Please try again.";

                toast.error(
                    message
                );
            } finally {
                setLoading(false);

                setTimeout(() => {
                    setUploadProgress(
                        0
                    );
                }, 500);
            }
        };

    /*
    |--------------------------------------------------------------------------
    | UI
    |--------------------------------------------------------------------------
    */

    return (
        <>
            <button
                type="button"
                className="dm-trigger"
                title="Open documents"
                onClick={() =>
                    setShow(true)
                }
            >
                <Eye size={14} />

                Documents

                {existingDocuments.length >
                    0 && (
                        <span className="dm-count">
                            {
                                existingDocuments.length
                            }
                        </span>
                    )}
            </button>

            <Modal
                show={show}
                onHide={
                    handleClose
                }
                backdrop={
                    loading
                        ? "static"
                        : true
                }
                keyboard={!loading}
                dialogClassName="dm-modal"
                size="xl"
                centered
            >
                <Modal.Header className="dm-header">
                    <div className="dm-header-main">
                        <div className="dm-header-icon">
                            <FileText
                                size={
                                    20
                                }
                            />
                        </div>

                        <div>
                            <Modal.Title className="dm-title">
                                {title}
                            </Modal.Title>

                            <p className="dm-subtitle">
                                {tab ===
                                    "add"
                                    ? "Upload and attach documents"
                                    : "Browse attached documents"}
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="dm-close"
                        onClick={
                            handleClose
                        }
                        aria-label="Close"
                        disabled={
                            loading
                        }
                    >
                        <X
                            size={
                                18
                            }
                        />
                    </button>
                </Modal.Header>

                <Modal.Body className="dm-body">
                    {/* Tabs */}

                    <div
                        className="dm-tabs"
                        role="tablist"
                    >
                        <button
                            type="button"
                            role="tab"
                            aria-selected={
                                tab ===
                                "add"
                            }
                            className="dm-tab"
                            onClick={() =>
                                setTab(
                                    "add"
                                )
                            }
                        >
                            <Upload
                                size={
                                    15
                                }
                            />

                            Upload
                        </button>

                        <button
                            type="button"
                            role="tab"
                            aria-selected={
                                tab ===
                                "view"
                            }
                            className="dm-tab"
                            onClick={() =>
                                setTab(
                                    "view"
                                )
                            }
                        >
                            <Eye
                                size={
                                    15
                                }
                            />

                            Attached

                            <span className="dm-count">
                                {
                                    documentList.length
                                }
                            </span>
                        </button>
                    </div>

                    {/* Upload */}

                    {tab ===
                        "add" && (
                            <form
                                onSubmit={
                                    handleSubmit
                                }
                                noValidate
                            >
                                <div className="dm-panel">
                                    <div className="dm-panel-head">
                                        <div>
                                            <h4>
                                                Document
                                                details
                                            </h4>

                                            <p>
                                                Accepted:
                                                ZIP,
                                                PDF,
                                                PNG,
                                                GIF,
                                                XLS,
                                                XLSX
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            className="dm-btn"
                                            onClick={
                                                addRow
                                            }
                                            disabled={
                                                loading
                                            }
                                        >
                                            <Plus
                                                size={
                                                    14
                                                }
                                            />

                                            Add
                                            another
                                        </button>
                                    </div>

                                    {rows.map(
                                        (
                                            r,
                                            i
                                        ) => {
                                            const nameErr =
                                                errors[
                                                `${r.id}-document_name`
                                                ];

                                            const typeErr =
                                                errors[
                                                `${r.id}-document_type`
                                                ];

                                            const fileErr =
                                                errors[
                                                `${r.id}-document_file`
                                                ];

                                            return (
                                                <div
                                                    key={
                                                        r.id
                                                    }
                                                    className="dm-row"
                                                >
                                                    {rows.length >
                                                        1 && (
                                                            <div className="dm-row-head">
                                                                <span className="dm-row-label">
                                                                    Document{" "}
                                                                    {i +
                                                                        1}
                                                                </span>

                                                                <button
                                                                    type="button"
                                                                    className="dm-btn dm-btn--danger dm-btn--sm"
                                                                    onClick={() =>
                                                                        removeRow(
                                                                            r.id
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        loading
                                                                    }
                                                                >
                                                                    <Trash2
                                                                        size={
                                                                            13
                                                                        }
                                                                    />

                                                                    Remove
                                                                </button>
                                                            </div>
                                                        )}

                                                    <div className="dm-grid">
                                                        {/* NAME */}

                                                        <div className="dm-field">
                                                            <label
                                                                htmlFor={`dm-name-${r.id}`}
                                                            >
                                                                Document
                                                                name

                                                                <span className="dm-req">
                                                                    *
                                                                </span>
                                                            </label>

                                                            <input
                                                                id={`dm-name-${r.id}`}
                                                                type="text"
                                                                className={`dm-input ${nameErr
                                                                    ? "is-invalid"
                                                                    : ""
                                                                    }`}
                                                                value={
                                                                    r.document_name
                                                                }
                                                                disabled={
                                                                    loading
                                                                }
                                                                onChange={(
                                                                    e
                                                                ) =>
                                                                    handleInputChange(
                                                                        r.id,
                                                                        "document_name",
                                                                        e
                                                                            .target
                                                                            .value
                                                                    )
                                                                }
                                                                placeholder="e.g. Work order – Phase 2"
                                                            />

                                                            {nameErr && (
                                                                <span className="dm-error">
                                                                    {
                                                                        nameErr
                                                                    }
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* TYPE */}

                                                        <div className="dm-field">
                                                            <label>
                                                                Document
                                                                type

                                                                <span className="dm-req">
                                                                    *
                                                                </span>
                                                            </label>

                                                            <Select
                                                                options={
                                                                    DOCUMENT_TYPE_OPTIONS
                                                                }
                                                                value={
                                                                    DOCUMENT_TYPE_OPTIONS.find(
                                                                        (
                                                                            o
                                                                        ) =>
                                                                            o.value ===
                                                                            r.document_type
                                                                    ) ||
                                                                    null
                                                                }
                                                                onChange={(
                                                                    o
                                                                ) =>
                                                                    handleInputChange(
                                                                        r.id,
                                                                        "document_type",
                                                                        o
                                                                            ? o.value
                                                                            : ""
                                                                    )
                                                                }
                                                                isSearchable
                                                                isClearable
                                                                isDisabled={
                                                                    loading
                                                                }
                                                                placeholder="Select type"
                                                                styles={
                                                                    CSSForSelectModern
                                                                }
                                                                menuPortalTarget={
                                                                    document.body
                                                                }
                                                            />

                                                            {typeErr && (
                                                                <span className="dm-error">
                                                                    {
                                                                        typeErr
                                                                    }
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* FILE */}

                                                        <div className="dm-field dm-field--full">
                                                            <label>
                                                                File

                                                                <span className="dm-req">
                                                                    *
                                                                </span>
                                                            </label>

                                                            {!r.document_file ? (
                                                                <label
                                                                    htmlFor={`dm-file-${r.id}`}
                                                                    className={`dm-drop ${dragOver ===
                                                                        r.id
                                                                        ? "is-over"
                                                                        : ""
                                                                        } ${fileErr
                                                                            ? "is-invalid"
                                                                            : ""
                                                                        }`}
                                                                    onDragOver={(
                                                                        e
                                                                    ) => {
                                                                        e.preventDefault();

                                                                        if (
                                                                            !loading
                                                                        ) {
                                                                            setDragOver(
                                                                                r.id
                                                                            );
                                                                        }
                                                                    }}
                                                                    onDragLeave={() =>
                                                                        setDragOver(
                                                                            null
                                                                        )
                                                                    }
                                                                    onDrop={(
                                                                        e
                                                                    ) => {
                                                                        e.preventDefault();

                                                                        if (
                                                                            loading
                                                                        )
                                                                            return;

                                                                        setDragOver(
                                                                            null
                                                                        );

                                                                        processFile(
                                                                            r.id,
                                                                            e
                                                                                .dataTransfer
                                                                                .files?.[0]
                                                                        );
                                                                    }}
                                                                >
                                                                    <FileUp
                                                                        size={
                                                                            22
                                                                        }
                                                                    />

                                                                    <span className="dm-drop-title">
                                                                        Drop
                                                                        a
                                                                        file
                                                                        here
                                                                        or{" "}
                                                                        <u>
                                                                            browse
                                                                        </u>
                                                                    </span>

                                                                    <span className="dm-drop-hint">
                                                                        ZIP,
                                                                        PDF,
                                                                        PNG,
                                                                        GIF,
                                                                        XLS,
                                                                        XLSX
                                                                    </span>
                                                                </label>
                                                            ) : (
                                                                <div className="dm-file">
                                                                    <div className="dm-file-icon">
                                                                        <FileCheck
                                                                            size={
                                                                                18
                                                                            }
                                                                        />
                                                                    </div>

                                                                    <div className="dm-file-info">
                                                                        <div
                                                                            className="dm-file-name"
                                                                            title={
                                                                                r.displayName
                                                                            }
                                                                        >
                                                                            {
                                                                                r.displayName
                                                                            }
                                                                        </div>

                                                                        <div className="dm-file-meta">
                                                                            {formatSize(
                                                                                r.size
                                                                            )}{" "}
                                                                            ·
                                                                            Ready
                                                                            to
                                                                            upload
                                                                        </div>
                                                                    </div>

                                                                    <button
                                                                        type="button"
                                                                        className="dm-btn dm-btn--sm"
                                                                        onClick={() =>
                                                                            clearFile(
                                                                                r.id
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            loading
                                                                        }
                                                                    >
                                                                        Replace
                                                                    </button>
                                                                </div>
                                                            )}

                                                            <input
                                                                id={`dm-file-${r.id}`}
                                                                type="file"
                                                                className="dm-file-input"
                                                                accept={
                                                                    ACCEPT
                                                                }
                                                                disabled={
                                                                    loading
                                                                }
                                                                ref={(
                                                                    el
                                                                ) => {
                                                                    if (
                                                                        el
                                                                    ) {
                                                                        fileInputRefs.current[
                                                                            r.id
                                                                        ] =
                                                                            el;
                                                                    }
                                                                }}
                                                                onChange={(
                                                                    e
                                                                ) =>
                                                                    processFile(
                                                                        r.id,
                                                                        e
                                                                            .target
                                                                            .files?.[0]
                                                                    )
                                                                }
                                                            />

                                                            {fileErr && (
                                                                <span className="dm-error">
                                                                    {
                                                                        fileErr
                                                                    }
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }
                                    )}

                                    {/* PROGRESS */}

                                    {uploadProgress >
                                        0 && (
                                            <div
                                                className="dm-progress"
                                                role="status"
                                            >
                                                <div className="dm-progress-label">
                                                    <span>
                                                        Uploading
                                                        documents
                                                    </span>

                                                    <strong>
                                                        {
                                                            uploadProgress
                                                        }
                                                        %
                                                    </strong>
                                                </div>

                                                <div className="dm-progress-track">
                                                    <div
                                                        className="dm-progress-fill"
                                                        style={{
                                                            width: `${uploadProgress}%`,
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                </div>

                                {/* FOOTER */}

                                <div className="dm-footer">
                                    <button
                                        type="button"
                                        className="dm-btn"
                                        onClick={
                                            handleClose
                                        }
                                        disabled={
                                            loading
                                        }
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="submit"
                                        className="dm-btn dm-btn--primary"
                                        disabled={
                                            loading
                                        }
                                    >
                                        {loading ? (
                                            <>
                                                <Loader
                                                    size={
                                                        15
                                                    }
                                                    className="spinning"
                                                />

                                                Uploading{" "}
                                                {
                                                    uploadProgress
                                                }
                                                %
                                            </>
                                        ) : rows.length >
                                            1 ? (
                                            `Upload ${rows.length} documents`
                                        ) : (
                                            "Upload document"
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}

                    {/* VIEW DOCUMENTS */}

                    {tab ===
                        "view" && (
                            <div className="dm-panel">
                                <div className="dm-table-card">
                                    <div className="dm-table-scroll">
                                        <table className="dm-table">
                                            <thead>
                                                <tr>
                                                    <th className="dm-col-num">
                                                        #
                                                    </th>

                                                    <th>
                                                        Document
                                                        name
                                                    </th>

                                                    <th>Type</th>

                                                    <th className="dm-col-center">
                                                        File
                                                    </th>

                                                    <th className="dm-col-center">
                                                        Delete
                                                    </th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {documentList.map(
                                                    (
                                                        doc,
                                                        i
                                                    ) => (
                                                        <tr
                                                            key={
                                                                doc.id ??
                                                                i
                                                            }
                                                        >
                                                            <td className="dm-col-num">
                                                                {i +
                                                                    1}
                                                            </td>

                                                            <td
                                                                className="dm-col-name"
                                                                title={
                                                                    doc.document_name
                                                                }
                                                            >
                                                                {
                                                                    doc.document_name
                                                                }
                                                            </td>

                                                            <td>
                                                                {doc.document_type ? (
                                                                    <span className="dm-badge">
                                                                        {typeLabel(
                                                                            doc.document_type
                                                                        )}
                                                                    </span>
                                                                ) : (
                                                                    "—"
                                                                )}
                                                            </td>

                                                            <td className="dm-col-center">
                                                                {doc.document_file ? (
                                                                    <a
                                                                        href={
                                                                            doc.document_file
                                                                        }
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="dm-link"
                                                                    >
                                                                        <FileCheck
                                                                            size={
                                                                                14
                                                                            }
                                                                        />

                                                                        Open
                                                                    </a>
                                                                ) : (
                                                                    <span className="dm-none">
                                                                        Not
                                                                        attached
                                                                    </span>
                                                                )}
                                                            </td>

                                                            <td className="dm-col-num">
                                                                <DeleteDocuments
                                                                    i={
                                                                        doc
                                                                    }
                                                                    getDocument={
                                                                        getDocumentList
                                                                    }
                                                                />
                                                            </td>
                                                        </tr>
                                                    )
                                                )}

                                                {documentList.length ===
                                                    0 && (
                                                        <tr>
                                                            <td
                                                                colSpan={
                                                                    5
                                                                }
                                                            >
                                                                <div className="dm-empty">
                                                                    <div className="dm-empty-icon">
                                                                        <FileSpreadsheet
                                                                            size={
                                                                                26
                                                                            }
                                                                        />
                                                                    </div>

                                                                    <h4>
                                                                        No
                                                                        documents
                                                                        yet
                                                                    </h4>

                                                                    <p>
                                                                        Upload
                                                                        your
                                                                        first
                                                                        document
                                                                        to
                                                                        see
                                                                        it
                                                                        here.
                                                                    </p>

                                                                    <button
                                                                        type="button"
                                                                        className="dm-btn dm-btn--primary"
                                                                        onClick={() =>
                                                                            setTab(
                                                                                "add"
                                                                            )
                                                                        }
                                                                    >
                                                                        <Paperclip
                                                                            size={
                                                                                14
                                                                            }
                                                                        />

                                                                        Upload
                                                                        document
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                </Modal.Body>
            </Modal>

            <ToastContainer
                position="top-center"
                autoClose={2000}
                newestOnTop
                closeOnClick
                pauseOnFocusLoss
                pauseOnHover
            />
        </>
    );
};

export { DocumentModel };